/**
 * Admin Issues API
 *
 * Zod schemas for the operational Issues tracking system.
 * Allows admins to list issues across all organizations and update their status.
 *
 * Base path: /api/admin-panel/issues
 *
 * Issues — Phase 1 (Backend)
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { ISSUE_OPERATOR_ACTION } from "../../../shared/domain/issues/issue-operator-action";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED CONSTANTS
// ═══════════════════════════════════════════════════════════════

export const ISSUE_STATUS_VALUES = ["OPEN", "RESOLVED", "IGNORED"] as const;
export type AdminIssueStatus = (typeof ISSUE_STATUS_VALUES)[number];

/**
 * The list's two orderings. `severity` is the triage view: critical first, then the most
 * recently seen. `lastSeen` is the activity view.
 */
export const ADMIN_ISSUE_SORT_VALUES = ["severity", "lastSeen"] as const;
export type AdminIssueSort = (typeof ADMIN_ISSUE_SORT_VALUES)[number];

/** Lowest to highest: the database enum's declared order, which is also its sort order. */
export const ADMIN_ISSUE_SEVERITY_VALUES = DbEnum.ISSUE_SEVERITY_VALUES;
export type AdminIssueSeverity = (typeof ADMIN_ISSUE_SEVERITY_VALUES)[number];
export const ADMIN_ISSUE_SCOPE_VALUES = DbEnum.ISSUE_SCOPE_VALUES;
export type AdminIssueScope = (typeof ADMIN_ISSUE_SCOPE_VALUES)[number];
export const ADMIN_ISSUE_SOURCE_VALUES = DbEnum.ISSUE_SOURCE_VALUES;
export type AdminIssueSource = (typeof ADMIN_ISSUE_SOURCE_VALUES)[number];

/**
 * The operator-runnable repairs, as a wire enum derived from the domain const so
 * the two can never drift. Spelling the values again here would let a key be
 * added to the domain and silently rejected at the API boundary.
 */
export const ISSUE_OPERATOR_ACTION_VALUES = Object.values(ISSUE_OPERATOR_ACTION) as [
  (typeof ISSUE_OPERATOR_ACTION)[keyof typeof ISSUE_OPERATOR_ACTION],
  ...(typeof ISSUE_OPERATOR_ACTION)[keyof typeof ISSUE_OPERATOR_ACTION][]
];

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const AdminIssueItemSchema = z.object({
  id: z.string(),
  /** Null on a PLATFORM issue, which belongs to no organization. */
  organizationId: z.string().nullable(),
  organizationName: z.string().nullable(),
  /** PLATFORM exactly when `organizationId` is null — a database CHECK holds the two equal. */
  scope: z.enum(ADMIN_ISSUE_SCOPE_VALUES),
  severity: z.enum(ADMIN_ISSUE_SEVERITY_VALUES),
  /** The runtime that reported the issue. */
  source: z.enum(ADMIN_ISSUE_SOURCE_VALUES),
  code: z.string(),
  category: z.string(),
  entityType: z.string(), // '' means no specific entity
  entityId: z.string(), // '' means no specific entity
  message: z.string(),
  /**
   * The operator runbook for this code, resolved from `ISSUE_REGISTRY` at read
   * time rather than read off the row — see `resolveIssueRemedy`. Null when the
   * code declares none, or when the row holds a code this build no longer knows.
   */
  remedy: z.string().nullable(),
  /**
   * The repair an operator can run from the Issues page, if the code declares
   * one. Also resolved at read time, and null for the same two reasons.
   */
  operatorAction: z.enum(ISSUE_OPERATOR_ACTION_VALUES).nullable(),
  rawPayload: z.unknown().nullable(),
  count: z.number().int(),
  status: z.enum(ISSUE_STATUS_VALUES),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  /**
   * When the row last became RESOLVED; null while it is not RESOLVED, and on a row resolved
   * before the column existed, since nothing recorded that moment.
   */
  resolvedAt: z.string().nullable()
});

export const ListIssuesResponseSchema = z.object({
  issues: z.array(AdminIssueItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int()
});

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const ListIssuesQuerySchema = z.object({
  status: z.enum(ISSUE_STATUS_VALUES).optional(),
  severity: z.enum(ADMIN_ISSUE_SEVERITY_VALUES).optional(),
  scope: z.enum(ADMIN_ISSUE_SCOPE_VALUES).optional(),
  source: z.enum(ADMIN_ISSUE_SOURCE_VALUES).optional(),
  category: z.string().optional(),
  organizationId: z.string().optional(),
  /**
   * Matches an organization by name (case-insensitive substring) or by exact id. A PLATFORM
   * issue has no organization, so a search never returns one.
   */
  search: z.string().trim().min(1).max(200).optional(),
  sort: z.enum(ADMIN_ISSUE_SORT_VALUES).default("lastSeen"),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(250).default(50)
});

export const UpdateIssueStatusBodySchema = z.object({
  status: z.enum(ISSUE_STATUS_VALUES)
});

export const IssueIdPathVarsSchema = z.object({
  id: z.string().uuid()
});

// ═══════════════════════════════════════════════════════════════
// API SCHEMA OBJECT
// ═══════════════════════════════════════════════════════════════

export const ZAdminIssues = {
  /**
   * List all issues (across all organizations, filterable)
   * GET /api/admin-panel/issues
   */
  ListIssues: {
    method: "GET" as const,
    path: "/api/admin-panel/issues",
    Params: ListIssuesQuerySchema,
    Response: ApiSuccessSchema(ListIssuesResponseSchema)
  },

  /**
   * One issue by id, in any organization or none — what a Slack page line links to.
   * GET /api/admin-panel/issues/:id
   */
  GetIssue: {
    method: "GET" as const,
    path: "/api/admin-panel/issues/:id",
    PathVars: IssueIdPathVarsSchema,
    Response: ApiSuccessSchema(AdminIssueItemSchema)
  },

  /**
   * Update an issue's status (OPEN → RESOLVED | IGNORED, etc.)
   * PATCH /api/admin-panel/issues/:id/status
   */
  UpdateIssueStatus: {
    method: "PATCH" as const,
    path: "/api/admin-panel/issues/:id/status",
    PathVars: IssueIdPathVarsSchema,
    Body: UpdateIssueStatusBodySchema,
    Response: ApiSuccessSchema(AdminIssueItemSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// EXPORTED TYPES
// ═══════════════════════════════════════════════════════════════

export type AdminIssueItem = z.infer<typeof AdminIssueItemSchema>;
export type ListIssuesQuery = z.infer<typeof ListIssuesQuerySchema>;
export type UpdateIssueStatusBody = z.infer<typeof UpdateIssueStatusBodySchema>;
export type ListIssuesResponse = z.infer<typeof ListIssuesResponseSchema>;
